import {
  Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FeedbackService, FeedbackRatings } from '../../services/feedback.service';
import { FeedbackUiService } from '../../services/feedback-ui.service';

/** Answer type for a survey question. */
type RatingQuestionType = 'scale' | 'boolean' | 'text';

/** A single survey question. `id` is the stable key sent to the backend. */
interface RatingQuestion {
  id:       string;
  text:     string;
  type:     RatingQuestionType;
  /** Scale anchors (scale type only). */
  low?:     string;
  high?:    string;
  /** Whether an answer is mandatory (drives progress + submit gating). */
  required: boolean;
}

/** Eight questions, in display order — keep `id`s/types in sync with the backend. */
const QUESTIONS: readonly RatingQuestion[] = [
  { id: 'targetingEase',            text: 'How easy was it to create your targeting sheet?',                                      type: 'scale',   low: 'Difficult',     high: 'Very easy',       required: true },
  { id: 'assistantExperience',      text: 'How would you rate your overall experience with the AI Planning Assistant (chatbot)?', type: 'scale',   low: 'Poor',          high: 'Excellent',       required: true },
  { id: 'betterDecisions',          text: 'Did the platform help you make better planning decisions?',                            type: 'boolean',                                               required: true },
  { id: 'featureFindability',       text: 'How easy was it to find the features you needed?',                                     type: 'scale',   low: 'Hard to find',  high: 'Effortless',      required: true },
  { id: 'reuseLikelihood',          text: 'How likely are you to use Planning Suite for your next plan?',                         type: 'scale',   low: 'Unlikely',      high: 'Very likely',     required: true },
  { id: 'recommendationConfidence', text: 'How confident are you in the accuracy of the recommendations?',                       type: 'scale',   low: 'Not confident', high: 'Fully confident', required: true },
  { id: 'timeSaved',                text: 'How much time did Planning Suite save you vs. your usual process?',                    type: 'text',                                                  required: false },
  { id: 'speedResponsiveness',      text: 'How would you rate the speed and responsiveness of the platform?',                     type: 'scale',   low: 'Slow',          high: 'Very fast',       required: true },
] as const;

/** External Google Form for richer/detailed feedback (opens in a new tab). */
const DETAILED_FEEDBACK_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSfpVhNqNPnSCnni4krEB8hs2IMvPBLdjnst_v5UiLA1mLaIAQ/viewform';

/**
 * "Feedback" rating survey — a short CSAT-style modal (six 1–5 questions plus an
 * optional comment), followed by a thank-you step. App-wide: mounted once in the
 * shell and opened from the user menu via FeedbackUiService. A fresh form is
 * shown every time it opens.
 */
@Component({
  selector: 'sp-feedback-rating',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './feedback-rating.component.html',
  styleUrls: ['./feedback-rating.component.scss'],
})
export class FeedbackRatingComponent implements OnInit, OnDestroy {
  readonly questions = QUESTIONS;
  readonly scale     = [1, 2, 3, 4, 5];
  readonly detailedFeedbackUrl = DETAILED_FEEDBACK_URL;

  open       = false;
  submitting = false;
  submitted  = false; // toggles the thank-you step
  error      = '';

  /** Answer per question id (undefined until answered). */
  answers: FeedbackRatings = {};
  comment = '';

  private submitSub?: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private feedback:   FeedbackService,
    private feedbackUi: FeedbackUiService,
    private cdr:        ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.feedbackUi.open$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.openModal());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.submitSub?.unsubscribe();
  }

  // ── Derived state ─────────────────────────────────────────────────────────
  /** Questions that must be answered before submit is enabled. */
  get requiredQuestions(): readonly RatingQuestion[] {
    return this.questions.filter(q => q.required);
  }

  /** True when a question has a valid answer (per its type). */
  isAnswered(q: RatingQuestion): boolean {
    const v = this.answers[q.id];
    if (q.type === 'scale')   return typeof v === 'number';
    if (q.type === 'boolean') return v === true || v === false;
    return typeof v === 'string' && v.trim().length > 0; // text
  }

  /** Count of required questions answered — drives the progress bar/footer. */
  get ratedCount(): number {
    return this.requiredQuestions.filter(q => this.isAnswered(q)).length;
  }

  get requiredCount(): number {
    return this.requiredQuestions.length;
  }

  get allRated(): boolean {
    return this.ratedCount === this.requiredCount;
  }

  /** Completion percentage (0–100) for the progress bar. */
  get progressPct(): number {
    return this.requiredCount ? (this.ratedCount / this.requiredCount) * 100 : 0;
  }

  // ── Open / close ──────────────────────────────────────────────────────────
  openModal(): void {
    this.reset();
    this.open = true;
    this.cdr.markForCheck();
  }

  closeModal(): void {
    if (this.submitting) return;
    this.open = false;
    this.cdr.markForCheck();
  }

  // ── Answering ────────────────────────────────────────────────────────────
  /** Set a scale (1–5) answer. */
  selectScale(question: string, value: number): void {
    this.answers[question] = value;
    this.error = '';
    this.cdr.markForCheck();
  }

  /** Set a Yes/No answer. */
  selectBoolean(question: string, value: boolean): void {
    this.answers[question] = value;
    this.error = '';
    this.cdr.markForCheck();
  }

  /** Free-text change (bound via ngModel). */
  onTextChange(): void {
    this.error = '';
    this.cdr.markForCheck();
  }

  // ── Submit ────────────────────────────────────────────────────────────────
  submit(): void {
    if (this.submitting || !this.allRated) return;

    // Build the payload from answered questions; trim free text, drop empties.
    const ratings: FeedbackRatings = {};
    for (const q of this.questions) {
      if (!this.isAnswered(q)) continue;
      const v = this.answers[q.id];
      ratings[q.id] = typeof v === 'string' ? v.trim() : v;
    }

    this.submitting = true;
    this.error      = '';
    this.cdr.markForCheck();

    this.submitSub = this.feedback
      .submit({ ratings, comment: this.comment.trim() })
      .subscribe({
        next: () => {
          this.submitting = false;
          this.submitted  = true; // show thank-you step
          this.cdr.markForCheck();
        },
        error: () => {
          this.submitting = false;
          this.error = 'Something went wrong while submitting. Please try again.';
          this.cdr.markForCheck();
        },
      });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  private reset(): void {
    this.submitSub?.unsubscribe();
    this.answers    = {};
    this.comment    = '';
    this.submitting = false;
    this.submitted  = false;
    this.error      = '';
  }
}
